import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';

export default function ProjectColumns({ board, api, onDataChange }) {
  const [columns, setColumns] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newColumn, setNewColumn] = useState({
    title: '',
    type: 'text',
    width: 150,
    settings: {},
    required: false
  });
  
  const [editingColumn, setEditingColumn] = useState(null);
  
  // States específicos para tipo "Status" 
  const [newStatusText, setNewStatusText] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  
  // States para tipo "Pessoa" - buscar membros do quadro
  const [boardMembers, setBoardMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Buscar membros do quadro quando board muda
  useEffect(() => {
    const fetchBoardMembers = async () => {
      if (!board?.id) return;
      
      try {
        setLoadingMembers(true);
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/projects/boards/${board.id}/members`);
        
        // Filtrar apenas membros ativos e formatear dados
        const activeMembers = response.data
          .filter(member => member.status === 'active' || member.status === 'pending')
          .map(member => ({
            id: member.user_id,
            name: member.user_name || member.user_email.split('@')[0], // Fallback para email se não tem nome
            email: member.user_email,
            avatar: member.user_name === 'Administrador do Sistema' ? '👨‍💼' : 
                   member.user_name?.includes('João') ? '👨‍💻' : 
                   member.user_name?.includes('Maria') ? '👩‍💼' : '👤',
            role: member.role,
            status: member.status
          }));
        
        setBoardMembers(activeMembers);
      } catch (error) {
        console.error('Erro ao buscar membros do quadro:', error);
        setBoardMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchBoardMembers();
  }, [board?.id]);

  const columnTypes = [
    { value: 'text', label: 'Texto', icon: '📝', description: 'Texto simples' },
    { value: 'status', label: 'Status', icon: '🔄', description: 'Lista de status personalizados' },
    { value: 'person', label: 'Pessoa', icon: '👤', description: 'Atribuir responsáveis' },
    { value: 'date', label: 'Data', icon: '📅', description: 'Datas e prazos' },
    { value: 'numbers', label: 'Números', icon: '🔢', description: 'Valores numéricos, moeda, percentual' },
    { value: 'dropdown', label: 'Lista Suspensa', icon: '📋', description: 'Opções predefinidas' },
    { value: 'progress', label: 'Progresso', icon: '📊', description: 'Barra de progresso visual' },
    { value: 'checkbox', label: 'Checkbox', icon: '☑️', description: 'Verdadeiro ou falso' },
    { value: 'time_tracking', label: 'Controle de Tempo', icon: '⏱️', description: 'Registrar tempo gasto em minutos' },
    { value: 'formula', label: 'Fórmula', icon: '🧮', description: 'Calcular valores automaticamente com expressões' },
    { value: 'file', label: 'Arquivo', icon: '📎', description: 'Anexar arquivos e documentos ao item' },
    { value: 'observacao', label: 'Observação', icon: '📝', description: 'Nota ou observação livre por item' }
  ];

  useEffect(() => {
    if (board) {
      fetchColumns();
    }
  }, [board]);

  const fetchColumns = async () => {
    if (!board) return;
    
    try {
      const response = await api.get(`/projects/boards/${board.id}/columns`);
      setColumns(response.data || []);
    } catch (error) {
      console.error('Error fetching columns:', error);
      setColumns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateColumn = async () => {
    if (!newColumn.title.trim()) {
      toast.error('Título da coluna é obrigatório');
      return;
    }

    try {
      const response = await api.post(`/projects/boards/${board.id}/columns`, newColumn);
      setColumns([...columns, response.data]);
      resetNewColumn();
      setShowCreateModal(false);
      toast.success('Coluna criada com sucesso!');
      
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error('Error creating column:', error);
      toast.error('Erro ao criar coluna');
    }
  };

  const handleUpdateColumn = async () => {
    if (!editingColumn || !editingColumn.title.trim()) {
      toast.error('Título da coluna é obrigatório');
      return;
    }

    try {
      const response = await api.put(`/projects/columns/${editingColumn.id}`, {
        title: editingColumn.title,
        type: editingColumn.type,
        width: editingColumn.width,
        settings: editingColumn.settings,
        required: editingColumn.required
      });
      
      setColumns(columns.map(col => col.id === editingColumn.id ? response.data : col));
      setEditingColumn(null);
      toast.success('Coluna atualizada com sucesso!');
      
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error('Error updating column:', error);
      toast.error('Erro ao atualizar coluna');
    }
  };

  const handleDeleteColumn = async (columnId) => {
    if (!confirm('Tem certeza que deseja excluir esta coluna? Todos os dados serão perdidos.')) return;

    try {
      await api.delete(`/projects/columns/${columnId}`);
      setColumns(columns.filter(col => col.id !== columnId));
      toast.success('Coluna excluída com sucesso!');
      
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error('Error deleting column:', error);
      toast.error('Erro ao excluir coluna');
    }
  };

  const resetNewColumn = () => {
    setNewColumn({
      title: '',
      type: 'text',
      width: 150,
      settings: {},
      required: false
    });
  };

  const getColumnTypeInfo = (type) => {
    return columnTypes.find(t => t.value === type) || columnTypes[0];
  };

  const renderColumnSettings = (column, isEditing = false) => {
    const updateSettings = (newSettings) => {
      if (isEditing) {
        setEditingColumn({
          ...editingColumn,
          settings: { ...editingColumn.settings, ...newSettings }
        });
      } else {
        setNewColumn({
          ...newColumn,
          settings: { ...newColumn.settings, ...newSettings }
        });
      }
    };

    const settings = isEditing ? editingColumn?.settings || {} : newColumn.settings;
    const columnType = isEditing ? editingColumn?.type : newColumn.type;

    if (columnType === 'dropdown') {
      return (
        <div className="space-y-2">
          <Label>Opções (uma por linha)</Label>
          <textarea
            className="w-full p-2 border rounded-md resize-vertical"
            rows={4}
            value={(settings.options || []).join('\n')}
            onChange={(e) => {
              // Processa o valor do textarea
              let inputValue = e.target.value;
              let options = [];
              
              console.log('Input value:', inputValue); // Debug
              
              // Verifica se contém separador "\"
              if (inputValue.includes('\\')) {
                console.log('Usando separador \\'); // Debug
                options = inputValue.split('\\').map(o => o.trim()).filter(o => o.length > 0);
              } else {
                console.log('Usando quebra de linha'); // Debug
                options = inputValue.split('\n').map(o => o.trim()).filter(o => o.length > 0);
              }
              
              console.log('Options processadas:', options); // Debug
              updateSettings({ options });
            }}
            placeholder="Digite cada opção em uma nova linha:&#10;Opção 1&#10;Opção 2&#10;Opção 3&#10;&#10;OU use separador \:&#10;Verde\Amarelo\Azul"
          />
          <p className="text-sm text-gray-500">
            Digite cada opção em uma nova linha OU separe com "\" (ex: Verde\Amarelo\Azul)
          </p>
        </div>
      );
    }

    if (columnType === 'status') {
      // Status pré-definidos
      const predefinedStatuses = [
        { label: 'Em progresso', value: 'Em progresso', color: 'bg-blue-100 text-blue-800', icon: '⚡' },
        { label: 'To Do', value: 'To Do', color: 'bg-gray-100 text-gray-800', icon: '📋' },
        { label: 'Working on it', value: 'Working on it', color: 'bg-orange-100 text-orange-800', icon: '🔥' },
        { label: 'Feito', value: 'Feito', color: 'bg-green-100 text-green-800', icon: '✅' },
        { label: 'Parado', value: 'Parado', color: 'bg-red-100 text-red-800', icon: '🚫' },
        { label: 'Pendente', value: 'Pendente', color: 'bg-yellow-100 text-yellow-800', icon: '⏸️' }
      ];

      const selectedStatuses = settings.options || ['Em progresso', 'To Do', 'Feito'];

      const toggleStatus = (statusValue) => {
        const isSelected = selectedStatuses.includes(statusValue);
        let newOptions;
        if (isSelected) {
          newOptions = selectedStatuses.filter(s => s !== statusValue);
        } else {
          newOptions = [...selectedStatuses, statusValue];
        }
        updateSettings({ options: newOptions });
      };

      const addCustomStatus = () => {
        if (newStatusText.trim() && !selectedStatuses.includes(newStatusText.trim())) {
          const newOptions = [...selectedStatuses, newStatusText.trim()];
          updateSettings({ options: newOptions });
          setNewStatusText('');
          setShowCustomInput(false);
        }
      };

      return (
        <div className="space-y-4">
          <Label>Status Disponíveis</Label>
          
          {/* Status pré-definidos */}
          <div className="grid grid-cols-2 gap-2">
            {predefinedStatuses.map((status) => {
              const isSelected = selectedStatuses.includes(status.value);
              return (
                <div
                  key={status.value}
                  onClick={() => toggleStatus(status.value)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    isSelected 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <span>{status.icon}</span>
                    <span className="font-medium">{status.label}</span>
                    {isSelected && <span className="text-blue-500 text-sm">✓</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Status personalizados já adicionados */}
          {selectedStatuses.filter(s => !predefinedStatuses.find(p => p.value === s)).map((customStatus) => (
            <div key={customStatus} className="flex items-center justify-between p-2 bg-purple-50 border border-purple-200 rounded">
              <span className="font-medium">🎯 {customStatus}</span>
              <button
                onClick={() => toggleStatus(customStatus)}
                className="text-red-500 hover:text-red-700"
              >
                ✕
              </button>
            </div>
          ))}

          {/* Botão adicionar status personalizado */}
          {!showCustomInput ? (
            <button
              onClick={() => setShowCustomInput(true)}
              className="w-full p-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
            >
              + Adicionar Status Personalizado
            </button>
          ) : (
            <div className="flex space-x-2">
              <input
                type="text"
                value={newStatusText}
                onChange={(e) => setNewStatusText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addCustomStatus()}
                placeholder="Nome do novo status"
                className="flex-1 p-2 border rounded-md"
                autoFocus
              />
              <button
                onClick={addCustomStatus}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                ✓
              </button>
              <button
                onClick={() => {
                  setShowCustomInput(false);
                  setNewStatusText('');
                }}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                ✕
              </button>
            </div>
          )}

          <p className="text-sm text-gray-500">
            Selecione os status que estarão disponíveis para esta coluna.
          </p>
        </div>
      );
    }

    if (columnType === 'person') {
      const selectedMembers = settings.members || boardMembers.map(m => m.id); // Por padrão, todos estão disponíveis

      const toggleMember = (memberId) => {
        const isSelected = selectedMembers.includes(memberId);
        let newMembers;
        if (isSelected && selectedMembers.length > 1) { // Pelo menos 1 deve estar selecionado
          newMembers = selectedMembers.filter(id => id !== memberId);
        } else if (!isSelected) {
          newMembers = [...selectedMembers, memberId];
        } else {
          return; // Não permite desmarcar o último
        }
        updateSettings({ members: newMembers });
      };

      if (loadingMembers) {
        return (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Carregando membros do quadro...</span>
          </div>
        );
      }

      if (boardMembers.length === 0) {
        return (
          <div className="text-center py-8">
            <p className="text-gray-600">Nenhum membro encontrado neste quadro.</p>
            <p className="text-sm text-gray-400 mt-2">
              Adicione membros ao quadro para usar colunas do tipo "Pessoa".
            </p>
          </div>
        );
      }

      return (
        <div className="space-y-4">
          <Label>Membros do Quadro</Label>
          <p className="text-sm text-gray-500">
            Selecione quais membros podem ser atribuídos a esta coluna.
          </p>
          
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {boardMembers.map((member) => {
              const isSelected = selectedMembers.includes(member.id);
              return (
                <div
                  key={member.id}
                  onClick={() => toggleMember(member.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    isSelected 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{member.avatar}</span>
                      <div>
                        <div className="font-medium flex items-center space-x-2">
                          <span>{member.name}</span>
                          {member.status === 'pending' && (
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                              Pendente
                            </span>
                          )}
                          {member.role === 'owner' && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                              Proprietário
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">{member.email}</div>
                      </div>
                    </div>
                    {isSelected && <span className="text-blue-500 text-lg">✓</span>}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="text-xs text-gray-400 mt-2">
            Total: {boardMembers.length} membros • Selecionados: {selectedMembers.length}
          </div>
        </div>
      );
    }

    if (columnType === 'formula') {
      const colOptions = (settings._availableCols || []).length > 0
        ? settings._availableCols
        : [];
      return (
        <div className="space-y-3">
          <label className="text-sm font-medium text-gray-700">Expressão da Fórmula</label>
          <textarea
            className="w-full p-2 border rounded-md resize-vertical font-mono text-sm"
            rows={3}
            value={settings.formula || ''}
            onChange={(e) => updateSettings({ formula: e.target.value })}
            placeholder='Exemplos:&#10;{coluna1} + {coluna2}&#10;IF({status} = "feito", 1, 0)&#10;{progresso} * 0.01'
          />
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-800 space-y-1">
            <p className="font-semibold">Referências de colunas:</p>
            <p>Use <code className="bg-blue-100 px-1 rounded">{'{'}id_da_coluna{'}'}</code> para referenciar valores de outras colunas.</p>
            <p className="font-semibold mt-2">Operadores suportados:</p>
            <p>Aritmética: <code className="bg-blue-100 px-1 rounded">+ - * /</code></p>
            <p>Concatenação: <code className="bg-blue-100 px-1 rounded">&</code></p>
            <p>Comparação: <code className="bg-blue-100 px-1 rounded">= != {'>'} {'<'} {'>'}= {'<'}=</code></p>
            <p>Condicional: <code className="bg-blue-100 px-1 rounded">IF(condição, se_verdadeiro, se_falso)</code></p>
            <p>Lógico: <code className="bg-blue-100 px-1 rounded">AND OR NOT</code></p>
          </div>
        </div>
      );
    }

    if (columnType === 'file') {
      return (
        <div className="space-y-3">
          <div className="bg-gray-50 border border-gray-200 rounded p-4 text-sm text-gray-700 space-y-2">
            <p className="font-semibold">📎 Coluna de Anexos</p>
            <p>Permite anexar arquivos diretamente aos itens do quadro.</p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Tamanho máximo: 25 MB por arquivo</li>
              <li>Qualquer tipo de arquivo é aceito</li>
              <li>Clique no ícone 📎 na tabela para fazer upload</li>
              <li>Arquivos ficam acessíveis a todos os membros do quadro</li>
            </ul>
          </div>
        </div>
      );
    }

    if (columnType === 'numbers') {
      return (
        <div className="space-y-3">
          <div>
            <Label>Formato</Label>
            <Select 
              value={settings.format || 'number'} 
              onValueChange={(value) => updateSettings({ format: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="number">Número</SelectItem>
                <SelectItem value="currency">Moeda (R$)</SelectItem>
                <SelectItem value="percentage">Porcentagem (%)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Valor Mínimo</Label>
              <Input
                type="number"
                value={settings.min_value || ''}
                onChange={(e) => updateSettings({ min_value: parseFloat(e.target.value) || null })}
                placeholder="0"
              />
            </div>
            <div>
              <Label>Valor Máximo</Label>
              <Input
                type="number"
                value={settings.max_value || ''}
                onChange={(e) => updateSettings({ max_value: parseFloat(e.target.value) || null })}
                placeholder="100"
              />
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando colunas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Colunas Personalizadas</h2>
          <p className="text-gray-600">Configure colunas customizadas para seus dados</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          Nova Coluna
        </Button>
      </div>

      {/* Columns List */}
      {columns.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v10z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma coluna personalizada</h3>
              <p className="text-gray-600 mb-6">
                Adicione colunas customizadas para organizar melhor suas informações
              </p>
              <Button onClick={() => setShowCreateModal(true)}>
                Criar Primeira Coluna
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {columns.map((column) => {
            const typeInfo = getColumnTypeInfo(column.type);
            return (
              <Card key={column.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="text-2xl">{typeInfo.icon}</div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{column.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{typeInfo.label}</p>
                        <div className="flex items-center space-x-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            Largura: {column.width}px
                          </Badge>
                          {column.required && (
                            <Badge variant="outline" className="text-xs text-red-600">
                              Obrigatório
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingColumn({...column})}
                        className="h-6 w-6 p-0"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteColumn(column.id)}
                        className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H9a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0">
                  <p className="text-sm text-gray-600">{typeInfo.description}</p>
                  
                  {column.settings && Object.keys(column.settings).length > 0 && (
                    <div className="mt-3 p-2 bg-gray-50 rounded text-xs">
                      <strong>Configurações:</strong> {JSON.stringify(column.settings)}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Column Modal */}
      <Dialog 
        open={showCreateModal} 
        onOpenChange={(open) => {
          setShowCreateModal(open);
          if (!open) resetNewColumn();
        }}
      >
        <DialogContent 
          className="max-w-2xl"
          onKeyDown={(e) => {
            // Permitir Enter nas textareas sem fechar o modal
            if (e.key === 'Enter' && e.target.tagName === 'TEXTAREA') {
              e.stopPropagation();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Criar Nova Coluna</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="column-title">Título da Coluna</Label>
              <Input
                id="column-title"
                value={newColumn.title}
                onChange={(e) => setNewColumn({...newColumn, title: e.target.value})}
                placeholder="Ex: Status do Projeto, Responsável, Prazo..."
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo da Coluna</Label>
              <div className="grid grid-cols-2 gap-2">
                {columnTypes.map((type) => (
                  <div
                    key={type.value}
                    onClick={() => setNewColumn({...newColumn, type: type.value, settings: {}})}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      newColumn.type === type.value 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start space-x-2">
                      <span className="text-lg">{type.icon}</span>
                      <div>
                        <div className="font-medium text-sm">{type.label}</div>
                        <div className="text-xs text-gray-600 mt-1">{type.description}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Column Settings */}
            {renderColumnSettings(newColumn)}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="column-width">Largura (px)</Label>
                <Input
                  id="column-width"
                  type="number"
                  value={newColumn.width}
                  onChange={(e) => setNewColumn({...newColumn, width: parseInt(e.target.value) || 150})}
                  min="100"
                  max="400"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Campo Obrigatório</Label>
                <div className="flex items-center space-x-2 mt-2">
                  <Switch 
                    checked={newColumn.required}
                    onCheckedChange={(checked) => setNewColumn({...newColumn, required: checked})}
                  />
                  <span className="text-sm">Obrigatório</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateColumn} disabled={!newColumn.title.trim()}>
                Criar Coluna
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Column Modal */}
      <Dialog 
        open={!!editingColumn} 
        onOpenChange={(open) => {
          if (!open) setEditingColumn(null);
        }}
      >
        <DialogContent 
          className="max-w-2xl"
          onKeyDown={(e) => {
            // Permitir Enter nas textareas sem fechar o modal
            if (e.key === 'Enter' && e.target.tagName === 'TEXTAREA') {
              e.stopPropagation();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Editar Coluna</DialogTitle>
          </DialogHeader>
          
          {editingColumn && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="edit-column-title">Título da Coluna</Label>
                <Input
                  id="edit-column-title"
                  value={editingColumn.title}
                  onChange={(e) => setEditingColumn({...editingColumn, title: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label>Tipo da Coluna</Label>
                <div className="grid grid-cols-2 gap-2">
                  {columnTypes.map((type) => (
                    <div
                      key={type.value}
                      onClick={() => setEditingColumn({...editingColumn, type: type.value, settings: {}})}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        editingColumn.type === type.value 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start space-x-2">
                        <span className="text-lg">{type.icon}</span>
                        <div>
                          <div className="font-medium text-sm">{type.label}</div>
                          <div className="text-xs text-gray-600 mt-1">{type.description}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Column Settings */}
              {renderColumnSettings(editingColumn, true)}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Largura (px)</Label>
                  <Input
                    type="number"
                    value={editingColumn.width}
                    onChange={(e) => setEditingColumn({...editingColumn, width: parseInt(e.target.value) || 150})}
                    min="100"
                    max="400"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Campo Obrigatório</Label>
                  <div className="flex items-center space-x-2 mt-2">
                    <Switch 
                      checked={editingColumn.required}
                      onCheckedChange={(checked) => setEditingColumn({...editingColumn, required: checked})}
                    />
                    <span className="text-sm">Obrigatório</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setEditingColumn(null)}>
                  Cancelar
                </Button>
                <Button onClick={handleUpdateColumn} disabled={!editingColumn.title.trim()}>
                  Salvar Alterações
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
